/**
 * Zen Diamond: Custom Search Panel
 * Extends Shopify's predictive search with trending/default state.
 */
if (!customElements.get('zen-search')) {
  class ZenSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.zen-search__input');
      this.closeBtn = this.querySelector('.zen-search__close');
      this.resultsContainer = this.querySelector('[data-zen-search-results]');
      this.trendingContainer = this.querySelector('[data-zen-search-trending]');
      this.statusElement = this.querySelector('.predictive-search-status');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.allInstances = document.querySelectorAll('zen-search');
    }

    connectedCallback() {
      if (!this.input) return;

      this.input.addEventListener(
        'input',
        this.debounce(this.onChange.bind(this), 300)
      );
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.closeBtn.addEventListener('click', this.onClose.bind(this));

      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      /* Trending search term clicks → navigate to search page */
    }

    debounce(fn, wait) {
      var timer;
      return function () {
        clearTimeout(timer);
        timer = setTimeout(fn, wait);
      };
    }

    onChange() {
      var newTerm = this.input.value.trim();

      this.updateSearchForTerm(this.searchTerm, newTerm);
      this.searchTerm = newTerm;

      if (!this.searchTerm.length) {
        this.closeResults();
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFocus() {
      var currentTerm = this.input.value.trim();
      if (!currentTerm.length) return;

      if (this.searchTerm !== currentTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFormSubmit(e) {
      if (!this.input.value.trim().length) e.preventDefault();
      var selected = this.querySelector('[aria-selected="true"] a');
      if (selected) {
        e.preventDefault();
        selected.click();
      }
    }

    onClose() {
      /* Clean up search state; details-modal handles closing the panel */
      this.searchTerm = '';
      this.input.value = '';
      this.closeResults();

      /* If details-modal doesn't close it, close manually */
      var modal = this.closest('details-modal');
      if (modal && modal.close) {
        modal.close(false);
      }
    }

    onKeyup(e) {
      if (!this.input.value.trim().length) this.closeResults();
      e.preventDefault();

      switch (e.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
        case 'Escape':
          this.onClose();
          break;
      }
    }

    onKeydown(e) {
      if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
        e.preventDefault();
      }
    }

    updateSearchForTerm(prevTerm, newTerm) {
      var el = this.querySelector('[data-predictive-search-search-for-text]');
      if (!el || !el.innerText) return;
      var currentText = el.innerText;
      if (prevTerm && currentText.match(new RegExp(prevTerm, 'g'))?.length > 1) return;
      if (prevTerm) {
        el.innerText = currentText.replace(prevTerm, newTerm);
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      var moveUp = direction === 'up';
      var selected = this.querySelector('[aria-selected="true"]');
      var allVisible = Array.from(
        this.resultsContainer.querySelectorAll('li[role="option"], a[role="option"], button[role="option"]')
      ).filter(function (el) { return el.offsetParent !== null; });

      if (!allVisible.length) return;
      if (moveUp && !selected) return;

      var selectedIdx = -1;
      for (var i = 0; i < allVisible.length; i++) {
        if (allVisible[i] === selected) { selectedIdx = i; break; }
      }

      var nextIdx = 0;
      if (!moveUp && selected) {
        nextIdx = selectedIdx === allVisible.length - 1 ? 0 : selectedIdx + 1;
      } else if (moveUp) {
        nextIdx = selectedIdx === 0 ? allVisible.length - 1 : selectedIdx - 1;
      }

      if (nextIdx === selectedIdx) return;

      var nextEl = allVisible[nextIdx];
      nextEl.setAttribute('aria-selected', 'true');
      if (selected) selected.setAttribute('aria-selected', 'false');
      this.input.setAttribute('aria-activedescendant', nextEl.id || '');
    }

    selectOption() {
      var selected = this.querySelector('[aria-selected="true"]');
      if (!selected) return;
      var link = selected.tagName === 'A' ? selected : selected.querySelector('a');
      if (link) link.click();
      else if (selected.tagName === 'BUTTON') selected.click();
    }

    getSearchResults(term) {
      var queryKey = term.replace(/ /g, '-').toLowerCase();
      this.setLoading();

      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      this.abortController.abort();
      this.abortController = new AbortController();

      fetch(
        routes.predictive_search_url +
          '?q=' + encodeURIComponent(term) +
          '&section_id=predictive-search',
        { signal: this.abortController.signal }
      )
        .then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then(function (text) {
          var markup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;

          this.allInstances.forEach(function (inst) {
            inst.cachedResults[queryKey] = markup;
          });
          this.renderResults(markup);
        }.bind(this))
        .catch(function (err) {
          if (err.code === 20) return;
          this.removeAttribute('loading');
        }.bind(this));
    }

    setLoading() {
      this.setAttribute('loading', '');
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'false');
        this.statusElement.textContent = this.getAttribute('data-loading-text') || 'Loading';
      }
    }

    renderResults(markup) {
      this.resultsContainer.innerHTML = markup;
      this.setAttribute('results', 'true');
      this.removeAttribute('loading');
      this.open();

      var countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (countEl && this.statusElement) {
        this.statusElement.textContent = countEl.textContent;
        setTimeout(function () {
          this.statusElement.setAttribute('aria-hidden', 'true');
        }.bind(this), 1000);
      }
    }

    open() {
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }

    closeResults() {
      this.removeAttribute('open');
      this.removeAttribute('loading');
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-activedescendant', '');
      this.isOpen = false;

      var selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');
    }
  }

  customElements.define('zen-search', ZenSearch);
}
